import { PromptConfig } from './prompt';
import type { SupportedLanguage } from './auth';
import type { ErrorParams } from '../errors/AppError';

export enum DocumentStatus {
  // Async-processing lifecycle (mirrors backend models.document.DocumentStatus).
  QUEUED = 'queued',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  // Legacy statuses kept for backward compatibility with older rows.
  UPLOADED = 'uploaded',
  PROCESSED = 'processed',
  ERROR = 'error'
}

/** Document sharing scope (TF-354/TF-620). Mirrors the backend DocumentVisibility enum. */
export enum DocumentVisibility {
  PRIVATE = 'private',
  TEAM = 'team',
  INSTITUTION = 'institution'
}

/**
 * Quality verdict reasons (TF-360). Mirrors backend
 * services.quality_assessor.QualityReason. Kept as a string union for
 * exhaustive UI mapping; `string` fallback tolerates future backend reasons.
 */
export type QualityReason =
  | 'ok'
  | 'scanned_low_text'
  | 'single_chunk_large_file'
  | 'garbage_extraction'
  | 'ocr_pages_discarded';

/**
 * Extraction-quality verdict exposed by the backend (TF-360) via
 * `Document.to_dict().quality`. `null` when no verdict was computed
 * (e.g. vectorisation failed before assessment).
 */
export interface DocumentQuality {
  ok: boolean;
  reason: QualityReason | string;
  signals?: Record<string, unknown>;
}

/**
 * OCR escalation state (TF-360/TF-365). Mirrors backend
 * services.quality_assessor.EscalationState. Exposed via
 * `Document.to_dict().escalation` so the UI can surface an in-flight or failed
 * OCR re-processing pass:
 *   - `queued`       — OCR re-processing queued/running (document still PROCESSED)
 *   - `failed`       — OCR re-processing failed (document flipped to ERROR)
 *   - `exhausted`    — OCR ran, quality still insufficient
 *   - `unavailable`  — escalation needed but OCR not available
 *   - `completed` / `not_needed` / `no_verdict` — no user-facing action
 * Closed union mirroring the backend Literal; an unknown value (deploy skew)
 * simply matches no badge branch and falls through to the default badges.
 */
export type DocumentEscalation =
  | 'queued'
  | 'completed'
  | 'exhausted'
  | 'unavailable'
  | 'failed'
  | 'not_needed'
  | 'no_verdict';

export interface Document {
  id: number;
  filename: string;
  original_filename: string;
  title: string;
  display_name?: string | null;
  mime_type: string;
  status: DocumentStatus;
  visibility?: DocumentVisibility;
  // Set only when visibility=team (TF-620).
  org_unit_id?: number | null;
  // Resolved OrgUnit.name, for display — set only when org_unit_id is set.
  org_unit_name?: string | null;
  created_at: string;
  processed_at?: string;
  file_size?: number;
  has_vectors: boolean;
  // Owner user id. Backend returns an int (DocumentResponse.user_id: Optional[int]).
  user_id?: number;
  content_preview?: string;
  vector_collection?: string;
  updated_at?: string;
  tags?: DocumentTag[];
  // OCR/quality escalation (TF-360/TF-361/TF-365). Backend populates these
  // fields file-format-independently (PDF + scanned DOCX) via Document.to_dict.
  processed_with_ocr?: boolean;
  quality?: DocumentQuality | null;
  // OCR escalation state (TF-365): surfaces an in-flight (`queued`) or
  // failed (`failed`) OCR re-processing pass in the UI. `null`/undefined
  // for legacy rows.
  escalation?: DocumentEscalation | null;
  metadata?: {
    total_chunks?: number;
    embedding_model?: string;
    processing_time?: number;
    error?: string;
    error_code?: string;
    error_details?: { filename?: string; mime_type?: string; [key: string]: any };
    vector_embedding_error?: string;
    [key: string]: any;
  };
}

export interface DocumentTag {
  id: number;
  name: string;
  scope: 'user' | 'institution' | 'global';
  is_own: boolean;
  // TF-399: true when this is a *personal* assignment of the current user
  // (document_personal_tags) rather than a shared institution assignment.
  // Only present on a document's `tags`; absent in the available-tags list.
  is_personal?: boolean;
}

export interface DocumentStats {
  total: number;
  processed: number;
  with_vectors: number;
  in_progress: number;
}

export type DocumentSort =
  | 'created_at_desc' | 'created_at_asc'
  | 'title_asc' | 'title_desc'
  | 'size_desc' | 'size_asc';

export type MimeFamily = 'pdf' | 'word' | 'markdown' | 'text' | 'chat';
export type StatusGroup = 'uploaded' | 'processing' | 'processed' | 'error';
export type VisibilityFilter = 'own' | 'shared';
export type ViewMode = 'cards' | 'list';

export interface DocumentListParams {
  q?: string;
  visibility?: VisibilityFilter;
  status?: StatusGroup[];
  mime_family?: MimeFamily[];
  tag_ids?: number[];
  sort?: DocumentSort;
  page?: number;
  page_size?: number;
}

export interface DocumentListResponse {
  documents: Document[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  stats: DocumentStats;
}

export interface DocumentUploadResponse {
  document_id: number;
  filename: string;
  message: string;
}

export interface DocumentProcessingResponse {
  message: string;
  document_id: number;
  processing_stats?: {
    total_chunks: number;
    embedding_dimension: number;
    model_name: string;
    processing_time: number;
  };
}

export interface AvailableDocumentsResponse {
  total_documents: number;
  processed_documents: number;
  documents_with_vectors: number;
  documents: Document[];
}

export interface RAGExamRequest {
  topic: string;
  /** Required and non-empty — the backend answers 422 otherwise (TF-969). */
  document_ids: number[];
  question_count: number;
  question_types: string[];
  difficulty: string;
  language: string;
  context_chunks_per_question?: number;
  prompt_config?: {
    single_choice?: PromptConfig;
    open_ended?: PromptConfig;
    true_false?: PromptConfig;
  };
  tag_ids?: number[];
  /** TF-400: competency framework ID; backend loads rendered_text when no override is set. */
  framework_id?: number;
  /** TF-400: edited competencies full text; takes precedence over framework_id (verbatim {{ competencies }}). */
  competencies_override?: string;
}

export interface RAGQuestion {
  question_text: string;
  question_type: string;
  options?: string[];
  correct_answer?: string;
  explanation?: string | string[];
  difficulty: string;
  source_chunks: string[];
  source_documents: string[];
  confidence_score: number;
  bloom_level?: number;
  estimated_time_minutes?: number;
  quality_tier?: string;
  review_status?: string;
  reviewed_by?: string;
  reviewed_at?: string;
}

export interface RAGContextSummary {
  query: string;
  total_chunks: number;
  total_similarity_score: number;
  source_documents: Array<{
    id: number;
    filename: string;
    /** TF-605: display name from the document library — prefer this over
     *  `filename` for display. Optional, since older responses don't carry it. */
    title?: string;
    chunks_used: number;
  }>;
  context_length: number;
  /** TF-736: only on POST /retrieve-context with `question_count` — the
   *  number of questions the generation would create from this material. */
  estimated_question_count?: number | null;
}

export interface RAGExamResponse {
  exam_id: string;
  topic: string;
  questions: RAGQuestion[];
  context_summary: RAGContextSummary;
  generation_time: number;
  review_question_ids?: number[];
  persistence_warning?: string;
  quality_metrics: {
    total_questions: number;
    average_confidence: number;
    source_coverage: number;
    question_type_distribution: Record<string, number>;
    context_chunks_used: number;
    total_context_length: number;
    average_similarity_score: number;
    // TF-358: requested_/generated_question_count are set on every RAG
    // generation. context_limited only when the question count was coupled
    // to the available chunk material (fewer questions generated than
    // requested); the notice text is built from the counts in the user's
    // language (TF-736). All optional (backward compat).
    requested_question_count?: number;
    generated_question_count?: number;
    context_limited?: boolean;
  };
}

export interface QuestionTypeInfo {
  type: string;
  name: string;
  description: string;
  example: string;
}

export interface DifficultyLevel {
  level: string;
  name: string;
  description: string;
}

// TF-626: was called `SupportedLanguage` and collided in the barrel
// (types/index.ts) with the identically-named union type from auth.ts
// ('de' | 'en' | 'fr' | 'it'). TypeScript resolved the ambiguity by
// dropping the name from the re-export entirely (TS2308) — both types
// were thus completely unreachable via `@examcraft/core/types`. This
// one here is an API DTO (entry in QuestionTypesResponse), not the
// user's language setting.
//
// TF-626-Review (suggestion): `code` used to be `string` — but the only
// actual producer (core/backend/api/rag_exams.py:supported_languages)
// exclusively returns values from exactly the same set as
// `SupportedLanguage` from auth.ts ('de' | 'en' | 'fr' | 'it'). Without
// this coupling, a typo in the backend ('deu', 'DE') would silently
// slip through here.
export interface SupportedLanguageOption {
  code: SupportedLanguage;
  name: string;
}

export interface QuestionTypesResponse {
  supported_types: QuestionTypeInfo[];
  difficulty_levels: DifficultyLevel[];
  supported_languages: SupportedLanguageOption[];
}

/**
 * TF-736: progress codes the backend sends (`schemas.task.ProgressCode`,
 * keep in sync). `progressMessageOf` renders them.
 */
export type KnownProgressCode =
  | 'generation_started'
  | 'context_loaded'
  | 'question_generated'
  | 'task_started'
  | 'task_retrying';

/** A known code, or one from a newer backend (falls back to `message`). */
export type ProgressCode = KnownProgressCode | (string & {});

/** State of a single generation task tracked by GenerationTasksContext */
export interface GenerationTaskState {
  taskId: string;
  status: 'PENDING' | 'STARTED' | 'PROGRESS' | 'SUCCESS' | 'FAILURE' | 'REVOKED' | 'RETRY' | 'UNKNOWN';
  progress: number;
  message: string | null;
  // TF-736: progress code + parameters from the backend; rendered via
  // `progressMessageOf` and preferred over `message`.
  messageCode?: ProgressCode | null;
  messageParams?: Record<string, unknown> | null;
  // TF-967: error code + parameters of a FAILURE/REVOKED, rendered via
  // `errorMessageOf` in the current language. `message` then holds the text
  // shown when the code is missing, unregistered or untranslated — usually
  // the backend's German `error`, otherwise a generic text resolved when the
  // failure arrived.
  errorCode?: string | null;
  errorParams?: ErrorParams | null;
  topic: string | null;
  questionCount: number | null;
  createdAt: string;
  result: RAGExamResponse | null;
  // TF-736: the job row's record of the outcome, from
  // GET /tasks/{task_id}/result. The only source left once the Celery result
  // (and with it `result.quality_metrics`) has expired.
  generatedQuestionCount?: number | null;
  contextLimited?: boolean;
}

/**
 * Response from GET /api/v1/rag/active-tasks.
 *
 * Since TF-608 contains not only running but also recently completed
 * tasks (terminal `status`), so a generation that finished during a
 * page change stays reachable. The result doesn't travel along — it's
 * fetched separately via `GET /api/v1/rag/tasks/{task_id}/result`.
 *
 * Since TF-736 also the caller's own under-filled SUCCESS jobs for up to 8
 * hours; jobs closed via `POST /tasks/{task_id}/dismiss` are left out.
 */
export interface ActiveTaskInfo {
  task_id: string;
  status: string;
  progress: number;
  message: string | null;
  // TF-736: see GenerationTaskState.messageCode. Optional because a
  // pre-TF-736 backend (rolling deploy) omits them.
  message_code?: ProgressCode | null;
  message_params?: Record<string, unknown> | null;
  created_at: string;
  topic: string | null;
  question_count: number | null;
}

export interface ActiveTasksResponse {
  tasks: ActiveTaskInfo[];
}

/** WebSocket message of `/ws/tasks/{task_id}` (`schemas.task.TaskStatusMessage`). */
export interface TaskStatusMessage {
  task_id: string;
  status: GenerationTaskState['status'];
  progress: number;
  message: string | null;
  message_code?: ProgressCode | null;
  message_params?: Record<string, unknown> | null;
  result?: RAGExamResponse | null;
  // German fallback text of a FAILURE/REVOKED.
  error?: string | null;
  // TF-967: error code (ADR 0005) + interpolation values; never on SUCCESS.
  error_code?: string | null;
  error_params?: Record<string, unknown> | null;
}

/**
 * Response from GET /api/v1/rag/tasks/{task_id}/result (TF-608).
 *
 * `result` is `null` when the Celery result backend has already
 * discarded the entry — the task stays visible then, just without a
 * detail view.
 */
export interface TaskResultResponse {
  task_id: string;
  status: string;
  result: RAGExamResponse | null;
  error: string | null;
  // TF-967: see TaskStatusMessage.error_code.
  error_code?: string | null;
  error_params?: Record<string, unknown> | null;
  // TF-736: read from the job row, set on SUCCESS. Same names as in
  // `quality_metrics`; `generated_question_count` is null for jobs recorded
  // before TF-736.
  requested_question_count?: number | null;
  generated_question_count?: number | null;
  context_limited?: boolean;
}

/** Context value exposed by GenerationTasksContext */
export interface GenerationTasksContextType {
  tasks: Record<string, GenerationTaskState>;
  activeTasks: GenerationTaskState[];
  completedTasks: GenerationTaskState[];
  startGeneration: (request: RAGExamRequest) => Promise<string>;
  retryTask: (taskId: string) => Promise<string>;
  dismissTask: (taskId: string) => void;
  getTask: (taskId: string) => GenerationTaskState | undefined;
}
